/**
 * Entity subscriptionModules helpers.
 * Extensible for future applications beyond SURESHIELD.
 */

export const SUBSCRIPTION_APPLICATIONS = Object.freeze({
  SURESHIELD: "SURESHIELD",
});

export const SUBSCRIPTION_MODULE_STATUS = Object.freeze({
  ACTIVE: "ACTIVE",
  TRIAL: "TRIAL",
  INACTIVE: "INACTIVE",
  EXPIRED: "EXPIRED",
  SUSPENDED: "SUSPENDED",
});

/** Statuses that mean the module is available to the tenant. */
export const SUBSCRIPTION_MODULE_AVAILABLE_STATUSES = Object.freeze([
  SUBSCRIPTION_MODULE_STATUS.ACTIVE,
  SUBSCRIPTION_MODULE_STATUS.TRIAL,
]);

const INTEGRITY_HEADER_LABEL = "Integrity";

export function getSubscriptionModules(entity) {
  return Array.isArray(entity?.subscriptionModules)
    ? entity.subscriptionModules
    : [];
}

export function getSubscriptionModule(entity, applicationName) {
  const target = String(applicationName || "").toUpperCase();
  if (!target) return null;
  return (
    getSubscriptionModules(entity).find(
      (module) =>
        String(module?.applicationName || "").toUpperCase() === target
    ) || null
  );
}

/**
 * Returns true when the named application is present in subscriptionModules
 * with enabled=true and an available status (ACTIVE or TRIAL by default).
 * Missing entry, enabled=false, or INACTIVE / EXPIRED / SUSPENDED => false.
 */
export function isSubscriptionModuleActive(
  entity,
  applicationName,
  {
    requireEnabled = true,
    availableStatuses = SUBSCRIPTION_MODULE_AVAILABLE_STATUSES,
  } = {}
) {
  const module = getSubscriptionModule(entity, applicationName);
  if (!module) return false;

  if (requireEnabled && module.enabled !== true) return false;

  const status = String(module.status || "").toUpperCase();
  const allowed = (availableStatuses || []).map((s) =>
    String(s || "").toUpperCase()
  );
  return allowed.includes(status);
}

export function isSureShieldSubscriptionActive(entity) {
  return isSubscriptionModuleActive(
    entity,
    SUBSCRIPTION_APPLICATIONS.SURESHIELD
  );
}

function parseCssLength(value) {
  const match = String(value || "")
    .trim()
    .match(/^(-?[\d.]+)(%|px|fr|em|rem)?$/i);
  if (!match) return null;
  return { amount: Number(match[1]), unit: (match[2] || "%").toLowerCase() };
}

function addCssLengths(a, b) {
  const left = parseCssLength(a);
  const right = parseCssLength(b);
  if (!left || !right || left.unit !== right.unit) {
    return a || b;
  }
  const sum = left.amount + right.amount;
  const formatted =
    Number.isInteger(sum) ? String(sum) : String(Math.round(sum * 100) / 100);
  return `${formatted}${left.unit}`;
}

/**
 * When Integrity is hidden, drop the matching header/column/sort entry and
 * fold that grid track's width into the previous column so layout stays intact.
 */
export function applyIntegrityColumnVisibility({
  headers = [],
  columns = [],
  sortValues,
  gridTemplateColumns,
  visible = true,
  headerLabel = INTEGRITY_HEADER_LABEL,
} = {}) {
  if (visible) {
    return {
      headers,
      columns,
      sortValues,
      gridTemplateColumns: undefined,
    };
  }

  const idx = headers.findIndex((header) => header === headerLabel);
  if (idx === -1) {
    return {
      headers,
      columns,
      sortValues,
      gridTemplateColumns: undefined,
    };
  }

  const nextHeaders = headers.filter((_, i) => i !== idx);
  const nextColumns = Array.isArray(columns)
    ? columns.filter((_, i) => i !== idx)
    : columns;
  const nextSort = Array.isArray(sortValues)
    ? sortValues.filter((_, i) => i !== idx)
    : sortValues;

  let nextGrid;
  if (
    typeof gridTemplateColumns === "string" &&
    gridTemplateColumns.trim()
  ) {
    const tracks = gridTemplateColumns.trim().split(/\s+/);
    if (tracks.length > idx) {
      const removed = tracks[idx];
      const rest = tracks.filter((_, i) => i !== idx);
      const mergeInto = Math.max(0, idx - 1);
      if (rest[mergeInto] != null) {
        rest[mergeInto] = addCssLengths(rest[mergeInto], removed);
      }
      nextGrid = rest.join(" ");
    }
  }

  // Keep column count and tracks aligned when no source template was provided.
  if (!nextGrid) {
    nextGrid = `repeat(${nextHeaders.length}, minmax(0, 1fr))`;
  }

  return {
    headers: nextHeaders,
    columns: nextColumns,
    sortValues: nextSort,
    gridTemplateColumns: nextGrid,
  };
}

export function filterIntegrityActions(actions = [], visible = true) {
  if (visible || !Array.isArray(actions)) return actions;
  return actions.filter(
    (action) =>
      !String(action?.data || "")
        .toLowerCase()
        .includes("integrity")
  );
}
